import { proxy, subscribe, useSnapshot } from 'valtio'

import { updateUserUiDataSettings } from '@/features/domain/ui'
import { selectRoutingLayout } from '@/features/domain/user'
import { isDeepEqual } from '@/server-data'
import { domainProxy, store } from '@/store'
import { debounce, unproxify } from '@/utils'

type LayoutMode = 'split' | 'map' | 'orders-grid' | 'scheduler'
type RoutingLayout = {
  mode: LayoutMode
  schedulerHeight: number
  bottomElement: 'scheduler' | 'orders-grid'
}
type RoutingLayoutAtom = { layout: RoutingLayout }

// ------------------------------------
// Default values
// ------------------------------------
const defaultRoutingLayout: RoutingLayout = {
  mode: 'split',
  schedulerHeight: 400,
  bottomElement: 'scheduler',
}

// ------------------------------------
// Routing layout atom
// ------------------------------------

function createDefaultRoutingLayout() {
  return { layout: defaultRoutingLayout }
}

const routingLayoutAtom = proxy<RoutingLayoutAtom>(createDefaultRoutingLayout())

export function subscribeRoutingLayout() {
  setRoutingLayout(selectRoutingLayout(store.getState()))

  domainProxy.subscribeToDataChangeSuccess(domainAction => {
    // If the domain action is fromSelf, it means that the action was triggered by the user
    // and we don't want to update the routing layout again
    if (domainAction.mode === 'update' && domainAction.fromSelf) return

    const newRoutingLayout = selectRoutingLayout(store.getState())
    if (isDeepEqual(newRoutingLayout, unproxify(routingLayoutAtom.layout))) return
    setRoutingLayout(newRoutingLayout)
  })

  subscribe(
    routingLayoutAtom.layout,
    debounce(() => {
      const storePreferences = selectRoutingLayout(store.getState())
      if (isDeepEqual(storePreferences, unproxify(routingLayoutAtom.layout))) return

      store.dispatch(
        updateUserUiDataSettings({
          routingLayout: unproxify(routingLayoutAtom.layout),
        }),
      )
    }, 2000),
  )
}

// ------------------------------------
// Write functions
// ------------------------------------

type SetRoutingLayout = (prev: RoutingLayout) => RoutingLayout
type SetRoutingLayoutParam = SetRoutingLayout | Partial<RoutingLayout>

export const setRoutingLayout = (valueOrFunc: SetRoutingLayoutParam) => {
  // callback with prev value
  if (typeof valueOrFunc === 'function') {
    Object.assign(routingLayoutAtom.layout, valueOrFunc(routingLayoutAtom.layout))
  } else {
    // atomic update
    for (const field of Object.keys(valueOrFunc)) {
      routingLayoutAtom.layout[field] = valueOrFunc[field]
    }
  }

  return routingLayoutAtom
}

// ------------------------------------
// React Hooks
// ------------------------------------

export const useRoutingLayout = () => {
  return [useSnapshot(routingLayoutAtom), setRoutingLayout] as const
}
